import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
  SearchOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  App,
  Button,
  Collapse,
  DatePicker,
  Form,
  Input,
  Modal,
  Result,
  Select,
  Skeleton,
  Table,
  Tag,
  Typography,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ApiError } from "../api/client";
import {
  fetchProjectDetail,
  fetchProjectMetadata,
  updateProject,
  updateProjectStatus,
  type ProjectDetail,
  type ProjectStatus,
  type ProjectWritePayload,
} from "../api/projects";
import { fetchRegistrants, type Registrant } from "../api/registrants";
import { useAuth } from "../features/auth/AuthContext";
import { CloudDriveInfoViewer } from "../features/projects/CloudDriveInfoViewer";
import { ProjectAttachmentWorkspace } from "../features/projects/ProjectAttachmentWorkspace";

interface ProjectDetailFormValues {
  work_name: string;
  work_type_code: string;
  rights_mode_code: string;
  completion_date: string;
  publication_date?: string;
  author_ids: string[];
  right_holder_ids: string[];
  cloud_drive_info?: string;
  work_summary?: string;
  creation_process?: string;
}

type PartyField = "right_holder_ids" | "author_ids";
const PROJECT_LONG_TEXT_LIMIT = 1500;

interface PartyPickerState {
  field: PartyField;
  title: "著作权人" | "作者";
  maxCount: 1 | 10;
}

const STATUS_LABELS: Record<ProjectStatus, string> = {
  SUBMITTED: "已提交",
  COMPLETED: "已完成",
};

function projectPartyLimit(rightsModeCode: string): 1 | 10 {
  return rightsModeCode === "COOPERATIVE_WORK" ? 10 : 1;
}

function nullable(value?: string) {
  const normalized = value?.trim();
  return normalized || null;
}

function formValuesFor(project: ProjectDetail): ProjectDetailFormValues {
  return {
    work_name: project.work_name,
    work_type_code: project.work_type_code,
    rights_mode_code: project.rights_mode_code,
    completion_date: project.completion_date,
    publication_date: project.publication_date ?? undefined,
    author_ids: project.authors.map((item) => item.registrant_id),
    right_holder_ids: project.right_holders.map((item) => item.registrant_id),
    cloud_drive_info: project.cloud_drive_info ?? undefined,
    work_summary: project.work_summary ?? undefined,
    creation_process: project.creation_process ?? undefined,
  };
}

function projectPayload(
  values: ProjectDetailFormValues,
  project: ProjectDetail,
): ProjectWritePayload {
  return {
    work_name: values.work_name.trim(),
    work_type_code: values.work_type_code,
    rights_mode_code: values.rights_mode_code,
    completion_date: values.completion_date,
    publication_date: values.publication_date || null,
    author_ids: values.author_ids,
    right_holder_ids: values.right_holder_ids,
    agent_name: project.agent_name,
    contact_phone: project.contact_phone,
    cloud_drive_info: nullable(values.cloud_drive_info),
    work_summary: (values.work_summary ?? "").trim(),
    creation_process: (values.creation_process ?? "").trim(),
  };
}

function errorMessage(error: unknown, fallback: string) {
  return error instanceof ApiError
    ? error.message
    : error instanceof Error
      ? error.message
      : fallback;
}

interface PartySelectionControlProps {
  id?: string;
  value?: string[];
  title: "著作权人" | "作者";
  partyNameById: Map<string, string>;
  onOpen: () => void;
}

function PartySelectionControl({
  id,
  value = [],
  title,
  partyNameById,
  onOpen,
}: PartySelectionControlProps) {
  if (!value.length) {
    return (
      <Button
        id={id}
        className="project-detail-party-add"
        block
        icon={<PlusOutlined />}
        onClick={onOpen}
      >
        添加{title}
      </Button>
    );
  }
  return (
    <button id={id} className="project-detail-party-selection" type="button" onClick={onOpen}>
      {value.map((registrantId) => (
        <span key={registrantId} title={partyNameById.get(registrantId)}>
          {partyNameById.get(registrantId) ?? "登记人已失效"}
        </span>
      ))}
    </button>
  );
}

export function ProjectDetailPage() {
  const { projectId = "" } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const canEdit = user?.role === "NORMAL_USER";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm<ProjectDetailFormValues>();
  const completionDate = Form.useWatch("completion_date", form);
  const [editing, setEditing] = useState(() => canEdit && searchParams.get("edit") === "1");
  const editFormInitialized = useRef(false);
  const [partyPicker, setPartyPicker] = useState<PartyPickerState | null>(null);
  const [partyDraftIds, setPartyDraftIds] = useState<string[]>([]);
  const [partySearch, setPartySearch] = useState("");
  const [partyPage, setPartyPage] = useState(1);
  const [partyPageSize, setPartyPageSize] = useState(10);
  const [partyCache, setPartyCache] = useState<Record<string, Registrant>>({});
  const deferredPartySearch = useDeferredValue(partySearch.trim());

  const projectQuery = useQuery({
    queryKey: ["project-detail", projectId],
    queryFn: () => fetchProjectDetail(projectId),
    enabled: Boolean(projectId),
    retry: (failureCount, error) =>
      !(error instanceof ApiError && [403, 404].includes(error.status)) && failureCount < 1,
  });
  const metadataQuery = useQuery({
    queryKey: ["project-metadata"],
    queryFn: ({ signal }) => fetchProjectMetadata(signal),
    staleTime: 30 * 60_000,
  });
  const registrantsQuery = useQuery({
    queryKey: ["project-detail-registrants", partyPage, partyPageSize, deferredPartySearch],
    queryFn: ({ signal }) =>
      fetchRegistrants(
        {
          page: partyPage,
          pageSize: partyPageSize,
          search: deferredPartySearch || undefined,
        },
        signal,
      ),
    enabled: Boolean(canEdit && partyPicker),
    staleTime: 30_000,
  });
  const project = projectQuery.data;
  const workTypeLabels = useMemo(
    () =>
      Object.fromEntries(
        metadataQuery.data?.work_types.map((item) => [item.code, item.label]) ?? [],
      ),
    [metadataQuery.data],
  );
  const rightsModeLabels = useMemo(
    () =>
      Object.fromEntries(
        metadataQuery.data?.rights_modes.map((item) => [item.code, item.label]) ?? [],
      ),
    [metadataQuery.data],
  );
  const partyNameById = useMemo(() => {
    const names = new Map<string, string>();
    project?.authors.forEach((item) => names.set(item.registrant_id, item.current_name));
    project?.right_holders.forEach((item) => names.set(item.registrant_id, item.current_name));
    Object.values(partyCache).forEach((item) => names.set(item.id, item.name));
    return names;
  }, [partyCache, project]);

  useEffect(() => {
    if (!project || !editing || editFormInitialized.current) return;
    form.setFieldsValue(formValuesFor(project));
    editFormInitialized.current = true;
  }, [editing, form, project]);

  const updateMutation = useMutation({
    mutationFn: (values: ProjectDetailFormValues) => {
      if (!project) throw new Error("项目详情尚未加载");
      return updateProject(project.id, {
        ...projectPayload(values, project),
        version: project.version,
      });
    },
    onSuccess: async (saved) => {
      queryClient.setQueryData(["project-detail", projectId], saved);
      setEditing(false);
      editFormInitialized.current = false;
      form.resetFields();
      message.success("项目资料已保存");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["projects"] }),
        queryClient.invalidateQueries({ queryKey: ["project-count"] }),
      ]);
    },
    onError: (error) => message.error(errorMessage(error, "项目保存失败")),
  });
  const statusMutation = useMutation({
    mutationFn: (status: ProjectStatus) => {
      if (!project) throw new Error("项目详情尚未加载");
      return updateProjectStatus(project.id, status, project.version);
    },
    onSuccess: async (saved) => {
      queryClient.setQueryData(["project-detail", projectId], saved);
      message.success(`项目已标记为${STATUS_LABELS[saved.status]}`);
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (error) => message.error(errorMessage(error, "项目状态更新失败")),
  });

  const backPath = isAdmin ? "/admin/projects" : "/projects";
  const startEditing = () => {
    if (!project) return;
    form.setFieldsValue(formValuesFor(project));
    editFormInitialized.current = true;
    setEditing(true);
  };
  const cancelEditing = () => {
    form.resetFields();
    editFormInitialized.current = false;
    setEditing(false);
  };
  const updateRightsMode = (value: string) => {
    const limit = projectPartyLimit(value);
    const authorIds = (form.getFieldValue("author_ids") as string[] | undefined) ?? [];
    const rightHolderIds = (form.getFieldValue("right_holder_ids") as string[] | undefined) ?? [];
    const trimmed = authorIds.length > limit || rightHolderIds.length > limit;
    form.setFieldsValue({
      author_ids: authorIds.slice(0, limit),
      right_holder_ids: rightHolderIds.slice(0, limit),
    });
    if (trimmed) message.info("非合作作品仅允许一位著作权人和一位作者，已保留首位");
  };
  const openPartyPicker = (field: PartyField) => {
    const rightsModeCode = form.getFieldValue("rights_mode_code") as string | undefined;
    const maxCount = projectPartyLimit(rightsModeCode ?? "");
    const selectedIds = (form.getFieldValue(field) as string[] | undefined) ?? [];
    setPartyPicker({
      field,
      title: field === "right_holder_ids" ? "著作权人" : "作者",
      maxCount,
    });
    setPartyDraftIds(selectedIds.slice(0, maxCount));
    setPartySearch("");
    setPartyPage(1);
  };
  const closePartyPicker = () => {
    setPartyPicker(null);
    setPartyDraftIds([]);
    setPartySearch("");
    setPartyPage(1);
  };
  const confirmPartyPicker = () => {
    if (!partyPicker) return;
    form.setFieldValue(partyPicker.field, partyDraftIds.slice(0, partyPicker.maxCount));
    closePartyPicker();
  };

  if (projectQuery.isPending) {
    return (
      <main className="project-detail-page">
        <Skeleton active paragraph={{ rows: 14 }} />
      </main>
    );
  }
  if (projectQuery.isError || !project) {
    const forbidden = projectQuery.error instanceof ApiError && projectQuery.error.status === 403;
    return (
      <main className="project-detail-page">
        <Result
          status={forbidden ? "403" : "404"}
          title={forbidden ? "无权查看该项目" : "项目不存在或已无法访问"}
          extra={<Button onClick={() => void navigate(backPath)}>返回项目列表</Button>}
        />
      </main>
    );
  }

  return (
    <main className="project-detail-page">
      <nav className="project-detail-breadcrumb" aria-label="面包屑">
        <button type="button" onClick={() => void navigate(backPath)}>
          项目管理
        </button>
        <span>/</span>
        <strong>项目详情</strong>
      </nav>
      <button className="project-detail-back" type="button" onClick={() => void navigate(backPath)}>
        <ArrowLeftOutlined /> 返回项目列表
      </button>

      <header className="project-detail-page-header">
        <div>
          <div className="project-detail-title-row">
            <Typography.Title level={1}>{project.work_name}</Typography.Title>
            <Tag
              className={`project-detail-status project-detail-status--${project.status.toLowerCase()}`}
              bordered={false}
            >
              {STATUS_LABELS[project.status]}
            </Tag>
            {project.is_deleted ? <Tag color="error">已删除</Tag> : null}
          </div>
          <p>
            项目编号：{project.project_number} · 创建于 {project.created_at.slice(0, 10)} · 最近更新{" "}
            {new Date(project.updated_at).toLocaleString("zh-CN", { hour12: false })}
            {isAdmin ? ` · 所属用户 ${project.owner_name}` : ""}
          </p>
        </div>
        {isAdmin && !project.is_deleted ? (
          <Button
            icon={project.status === "COMPLETED" ? <UndoOutlined /> : <CheckCircleOutlined />}
            loading={statusMutation.isPending}
            onClick={() =>
              statusMutation.mutate(project.status === "COMPLETED" ? "SUBMITTED" : "COMPLETED")
            }
          >
            标记为{project.status === "COMPLETED" ? "已提交" : "已完成"}
          </Button>
        ) : null}
      </header>

      <div className="project-detail-layout">
        <section className="project-detail-info-panel" aria-label="项目信息">
          <header>
            <Typography.Title level={3}>项目信息</Typography.Title>
            {canEdit && !project.is_deleted && !editing ? (
              <Button icon={<EditOutlined />} onClick={startEditing}>
                编辑
              </Button>
            ) : null}
            {editing ? (
              <div className="project-detail-edit-actions">
                <Button onClick={cancelEditing}>取消</Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={updateMutation.isPending}
                  onClick={() => form.submit()}
                >
                  保存
                </Button>
              </div>
            ) : null}
          </header>

          {editing ? (
            <Form<ProjectDetailFormValues>
              className="project-detail-edit-form"
              form={form}
              layout="vertical"
              requiredMark="optional"
              onFinish={(values) => updateMutation.mutate(values)}
            >
              <Form.Item name="work_name" label="作品名称" rules={[{ required: true, max: 200 }]}>
                <Input placeholder="请输入作品名称" />
              </Form.Item>
              <div className="project-detail-form-grid">
                <Form.Item name="work_type_code" label="作品类型" rules={[{ required: true }]}>
                  <Select
                    options={metadataQuery.data?.work_types.map((item) => ({
                      value: item.code,
                      label: item.label,
                    }))}
                  />
                </Form.Item>
                <Form.Item
                  name="rights_mode_code"
                  label="权利归属方式"
                  rules={[{ required: true }]}
                >
                  <Select
                    options={metadataQuery.data?.rights_modes.map((item) => ({
                      value: item.code,
                      label: item.label,
                    }))}
                    onChange={updateRightsMode}
                  />
                </Form.Item>
                <Form.Item
                  name="completion_date"
                  label="创作完成日期"
                  rules={[{ required: true }]}
                  getValueProps={(value?: string) => ({ value: value ? dayjs(value) : null })}
                  getValueFromEvent={(value: Dayjs | null) => value?.format("YYYY-MM-DD") ?? ""}
                >
                  <DatePicker
                    allowClear
                    maxDate={dayjs()}
                    format="YYYY-MM-DD"
                    placeholder="请选择"
                  />
                </Form.Item>
                <Form.Item
                  name="publication_date"
                  label="创作发表日期"
                  getValueProps={(value?: string) => ({ value: value ? dayjs(value) : null })}
                  getValueFromEvent={(value: Dayjs | null) => value?.format("YYYY-MM-DD") ?? ""}
                >
                  <DatePicker
                    allowClear
                    maxDate={dayjs()}
                    minDate={completionDate ? dayjs(completionDate) : undefined}
                    format="YYYY-MM-DD"
                    placeholder="不填表示未发表"
                  />
                </Form.Item>
              </div>
              <Form.Item
                name="right_holder_ids"
                label="著作权人"
                rules={[
                  { required: true, type: "array", min: 1, message: "请选择著作权人" },
                  {
                    validator: (_, value: string[] = []) => {
                      const limit = projectPartyLimit(
                        (form.getFieldValue("rights_mode_code") as string | undefined) ?? "",
                      );
                      return value.length <= limit
                        ? Promise.resolve()
                        : Promise.reject(new Error(`当前权利归属方式最多选择 ${limit} 人`));
                    },
                  },
                ]}
              >
                <PartySelectionControl
                  title="著作权人"
                  partyNameById={partyNameById}
                  onOpen={() => openPartyPicker("right_holder_ids")}
                />
              </Form.Item>
              <Form.Item
                name="author_ids"
                label="作者"
                rules={[
                  { required: true, type: "array", min: 1, message: "请选择作者" },
                  {
                    validator: (_, value: string[] = []) => {
                      const limit = projectPartyLimit(
                        (form.getFieldValue("rights_mode_code") as string | undefined) ?? "",
                      );
                      return value.length <= limit
                        ? Promise.resolve()
                        : Promise.reject(new Error(`当前权利归属方式最多选择 ${limit} 人`));
                    },
                  },
                ]}
              >
                <PartySelectionControl
                  title="作者"
                  partyNameById={partyNameById}
                  onOpen={() => openPartyPicker("author_ids")}
                />
              </Form.Item>
              <Form.Item name="cloud_drive_info" label="网盘信息">
                <Input.TextArea
                  rows={3}
                  maxLength={500}
                  showCount
                  placeholder="请输入网盘分享链接、提取码或访问说明"
                />
              </Form.Item>
              <Form.Item
                name="work_summary"
                label="作品简介"
                rules={[
                  { required: true, whitespace: true, message: "请输入作品简介" },
                  {
                    max: PROJECT_LONG_TEXT_LIMIT,
                    message: `最多输入 ${PROJECT_LONG_TEXT_LIMIT} 字`,
                  },
                ]}
              >
                <Input.TextArea
                  rows={4}
                  maxLength={PROJECT_LONG_TEXT_LIMIT}
                  showCount
                  placeholder="请说明作品的主要内容、主题、表现形式和创作意图，建议字数在 200 字左右。"
                />
              </Form.Item>
              <Form.Item
                name="creation_process"
                label="创作过程"
                rules={[
                  { required: true, whitespace: true, message: "请输入创作过程" },
                  {
                    max: PROJECT_LONG_TEXT_LIMIT,
                    message: `最多输入 ${PROJECT_LONG_TEXT_LIMIT} 字`,
                  },
                ]}
              >
                <Input.TextArea
                  rows={4}
                  maxLength={PROJECT_LONG_TEXT_LIMIT}
                  showCount
                  placeholder="请说明创作起因、构思过程、主要工具或方法、修改完善过程，建议字数在 200 字左右。"
                />
              </Form.Item>
            </Form>
          ) : (
            <>
              <dl className="project-detail-definition-list">
                <div>
                  <dt>作品名称</dt>
                  <dd>{project.work_name}</dd>
                </div>
                <div>
                  <dt>作品类型</dt>
                  <dd>{workTypeLabels[project.work_type_code] ?? project.work_type_code}</dd>
                </div>
                <div>
                  <dt>权利归属方式</dt>
                  <dd>{rightsModeLabels[project.rights_mode_code] ?? project.rights_mode_code}</dd>
                </div>
                <div>
                  <dt>创作完成日期</dt>
                  <dd>{project.completion_date}</dd>
                </div>
                <div>
                  <dt>创作发表日期</dt>
                  <dd>{project.publication_date ?? "未发表"}</dd>
                </div>
                <div>
                  <dt>著作权人</dt>
                  <dd>{project.right_holders.map((item) => item.current_name).join("、")}</dd>
                </div>
                <div>
                  <dt>作者</dt>
                  <dd>{project.authors.map((item) => item.current_name).join("、")}</dd>
                </div>
                <div>
                  <dt>网盘信息</dt>
                  <dd>
                    {project.cloud_drive_info?.trim() ? (
                      <CloudDriveInfoViewer projectId={project.id} />
                    ) : (
                      "—"
                    )}
                  </dd>
                </div>
              </dl>
              <Collapse
                className="project-detail-notes"
                defaultActiveKey={["summary", "process"]}
                items={[
                  {
                    key: "summary",
                    label: "作品简介",
                    children: <p>{project.work_summary ?? "暂无内容"}</p>,
                  },
                  {
                    key: "process",
                    label: "创作过程",
                    children: <p>{project.creation_process ?? "暂无内容"}</p>,
                  },
                ]}
              />
            </>
          )}
        </section>

        <section className="project-detail-attachment-panel" aria-label="项目附件资料">
          {project.is_deleted ? (
            <Alert type="warning" showIcon title="已删除项目不提供附件访问" />
          ) : (
            <ProjectAttachmentWorkspace
              projectId={project.id}
              canUpload={canEdit}
              canManage={canEdit}
            />
          )}
        </section>
      </div>

      <Modal
        className="batch-party-picker-modal"
        title={`选择${partyPicker?.title ?? "登记人"}`}
        open={Boolean(partyPicker)}
        width={720}
        zIndex={1200}
        okText="确认选择"
        cancelText="取消"
        onOk={confirmPartyPicker}
        onCancel={closePartyPicker}
      >
        <div className="batch-party-picker-toolbar">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            value={partySearch}
            placeholder="搜索登记人名称、编号或证件号码"
            onChange={(event) => {
              setPartySearch(event.target.value);
              setPartyPage(1);
            }}
          />
          <span>
            已选择 {partyDraftIds.length}/{partyPicker?.maxCount ?? 1}
          </span>
        </div>
        <Table<Registrant>
          className="batch-party-picker-table"
          rowKey="id"
          size="middle"
          loading={registrantsQuery.isPending || registrantsQuery.isFetching}
          dataSource={registrantsQuery.data?.items ?? []}
          pagination={{
            current: partyPage,
            pageSize: partyPageSize,
            total: registrantsQuery.data?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            showTotal: (total) => `共 ${total} 个登记人`,
            onChange: (nextPage, nextPageSize) => {
              setPartyPageSize(nextPageSize);
              setPartyPage(nextPageSize === partyPageSize ? nextPage : 1);
            },
          }}
          scroll={{ y: 320 }}
          locale={{ emptyText: "暂无可选择的登记人" }}
          rowSelection={{
            type: partyPicker?.maxCount === 1 ? "radio" : "checkbox",
            selectedRowKeys: partyDraftIds,
            preserveSelectedRowKeys: true,
            onChange: (keys, selectedRows) => {
              const nextIds = keys.map(String);
              const maxCount = partyPicker?.maxCount ?? 1;
              if (nextIds.length > maxCount) {
                message.warning(`当前权利归属方式最多选择 ${maxCount} 个登记人`);
                return;
              }
              setPartyCache((current) => ({
                ...current,
                ...Object.fromEntries(selectedRows.map((record) => [record.id, record])),
              }));
              setPartyDraftIds(nextIds);
            },
            getCheckboxProps: (record) => ({
              disabled:
                (partyPicker?.maxCount ?? 1) > 1 &&
                partyDraftIds.length >= (partyPicker?.maxCount ?? 1) &&
                !partyDraftIds.includes(record.id),
            }),
          }}
          columns={[
            { title: "登记人名称", dataIndex: "name", ellipsis: true },
            {
              title: "登记人类别",
              dataIndex: "registrant_type",
              width: 160,
              render: (value: Registrant["registrant_type"]) =>
                value === "NATURAL_PERSON" ? "自然人" : "法人或其他组织",
            },
            {
              title: "证件号码",
              dataIndex: "certificate_number_masked",
              width: 190,
            },
          ]}
        />
      </Modal>
    </main>
  );
}
